import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll', 'click',
] as const;

function getTimeoutMs(): number {
  try {
    const prefs = JSON.parse(localStorage.getItem('cn_user_preferences') || '{}');
    const minutes = prefs.sessionTimeout ?? 15;
    return Number(minutes) * 60 * 1000;
  } catch {
    return 15 * 60 * 1000;
  }
}

export function useInactivityLogout() {
  const { user, logout } = useAuth();
  const { showWarning } = useNotification();
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doLogout = useCallback(async () => {
    await logout('inactivity');
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    const ms = getTimeoutMs();
    if (!ms) return; // 0 = nunca

    // Aviso 1 minuto antes (solo si timeout > 2 min)
    if (ms > 2 * 60 * 1000) {
      warningRef.current = setTimeout(() => {
        showWarning('Tu sesión expirará en 1 minuto por inactividad.');
      }, ms - 60 * 1000);
    }

    timerRef.current = setTimeout(doLogout, ms);
  }, [doLogout, showWarning]);

  useEffect(() => {
    if (!user) return;

    resetTimer();

    const onActivity = () => resetTimer();
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
    };
  }, [user, resetTimer]);
}
